import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { BUSINESS, REVIEWS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = () => (
  <div className="flex gap-0.5" aria-hidden="true">
    {Array.from({ length: 5 }).map((_, j) => (
      <Star key={j} className="h-3.5 w-3.5 fill-mint text-mint" />
    ))}
  </div>
);

const ReviewsCarousel = () => {
  const [live, setLive] = useState(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: true });

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API}/reviews`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("api"))))
      .then(setLive)
      .catch((e) => e.name !== "AbortError" && setLive({ available: false }));
    return () => controller.abort();
  }, []);

  const isLive = Boolean(live?.available && live.reviews?.length);
  const cards = isLive
    ? live.reviews.map((r) => ({ author: r.author || "Google user", text: r.text, relative_time: r.relative_time, placeholder: false }))
    : REVIEWS;
  const rating = isLive ? String(live.rating ?? BUSINESS.rating) : BUSINESS.rating;
  const count = isLive ? live.review_count ?? BUSINESS.reviewCount : BUSINESS.reviewCount;

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <div data-testid="reviews-carousel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="kicker">Proof</p>
          <h2 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl" data-testid="reviews-headline">
            {rating} stars. {count} reviews.
          </h2>
          {isLive && <p className="spec-label mt-2 text-mint">Live from Google</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1" aria-label={`${rating} out of 5 stars on Google`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-mint text-mint" aria-hidden="true" />
            ))}
          </div>
          <button onClick={scrollPrev} data-testid="reviews-prev-btn" aria-label="Previous reviews" className="border border-teal-line p-2.5 text-chrome transition-colors duration-200 hover:border-mint hover:text-mint">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={scrollNext} data-testid="reviews-next-btn" aria-label="Next reviews" className="border border-teal-line p-2.5 text-chrome transition-colors duration-200 hover:border-mint hover:text-mint">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-14 overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {cards.map((r, i) => (
            <div key={`${r.author}-${i}`} className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)]">
              <div className={`flex h-full flex-col border p-7 ${r.placeholder ? "border-dashed border-teal-line bg-transparent" : "border-teal-line bg-panel"}`}>
                <Stars />
                <p className="mt-4 flex-1 text-sm leading-relaxed text-white/90">"{r.text}"</p>
                <p className="spec-label mt-5">{r.author}</p>
                {r.relative_time && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-chrome/60">{r.relative_time}</p>}
                {r.placeholder && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-mint/60">Placeholder</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {isLive && <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-chrome/50">Reviews from Google</p>}
    </div>
  );
};

export default ReviewsCarousel;
